import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import Input from "../../Shared/Input";
import FormFooter from "../../Shared/FormFooter";
import TabFormWrapper from "../../Shared/TabFormWrapper";
import AlertRed from "../../Shared/AlertRed";
import AlertModal from "../../Shared/AlertModal";
import { closeAccount } from "../../../api/settingsApi";
import { CLOSE_ACCOUNT_CONFIRM_PHRASE } from "../../../config/constants";

const DeleteAccountForm = () => {
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Stores the validated form data while the confirmation modal is open
  const [pendingData, setPendingData] = useState(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      password: "",
      confirmPhrase: "",
    },
  });

  const { mutate: doCloseAccount, isPending: isClosing } = useMutation({
    mutationFn: closeAccount,
    onSuccess: () => {
      // Clear all React Query cache and navigate directly, without calling
      // AuthContext.logout(). The server already destroyed every session for
      // this account, so a /auth/logout call would be redundant. If a request
      // that was already in flight returns 401 after the session is gone, the
      // global 401 interceptor (apiConfig.js) is a no-op here because we have
      // already navigated to "/signin", which is in its auth-path allowlist.
      queryClient.clear();
      navigate("/signin");
    },
    onError: (err) => {
      setError({
        title: "Error",
        message:
          err.response?.data?.error?.message ||
          "Failed to close account. Please try again.",
      });
    },
  });

  const onSubmit = (data) => {
    setError(null);
    setPendingData(data);
    setIsModalOpen(true);
  };

  const handleModalConfirm = () => {
    if (!pendingData || isClosing) return;
    setIsModalOpen(false);
    doCloseAccount({
      password: pendingData.password,
      confirmPhrase: pendingData.confirmPhrase,
    });
    // Don't keep the plaintext password in state after dispatching
    setPendingData(null);
  };

  const handleModalDismiss = () => {
    setIsModalOpen(false);
    setPendingData(null);
  };

  return (
    <>
      <TabFormWrapper onSubmit={handleSubmit(onSubmit)}>
        {/* Danger warning callout */}
        <div className="rounded-md bg-signal-red-50 border border-signal-red-100 p-4 mb-6">
          <div className="flex">
            <svg
              className="h-5 w-5 text-signal-red-500 shrink-0"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.168-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.457-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
            <p className="ml-3 text-sm text-signal-red-600">
              Closing your account signs you out everywhere and immediately
              disables your booking page and login. Your data is retained and
              can be restored by contacting support at{" "}
              <span className="font-medium">support@mail.anyspot.io</span>.
              Permanent deletion of closed accounts will follow our
              data-retention policy.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
          <div className="col-span-full">
            <Input
              id="password"
              name="password"
              label="Your password"
              type="password"
              errormessage={errors?.password?.message}
              {...register("password", {
                required: "Password is required to confirm this action",
              })}
            />
          </div>
          <div className="col-span-full">
            <Input
              id="confirmPhrase"
              name="confirmPhrase"
              label={`Type "${CLOSE_ACCOUNT_CONFIRM_PHRASE}" to confirm`}
              type="text"
              placeholder={CLOSE_ACCOUNT_CONFIRM_PHRASE}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              errormessage={errors?.confirmPhrase?.message}
              {...register("confirmPhrase", {
                required: "Confirmation phrase is required",
                validate: (value) =>
                  value.trim().toLowerCase() === CLOSE_ACCOUNT_CONFIRM_PHRASE ||
                  `Phrase does not match — type: ${CLOSE_ACCOUNT_CONFIRM_PHRASE}`,
              })}
            />
          </div>
        </div>

        <FormFooter
          primaryCaption="Close my account"
          primaryButtonColor="red"
          isLoading={isClosing}
          loadingCaption="Closing…"
          requiresVerification={false}
        />

        {error ? (
          <div className="mt-4">
            <AlertRed title={error.title} message={error.message} />
          </div>
        ) : null}
      </TabFormWrapper>

      <AlertModal
        isOpen={isModalOpen}
        modalTitle="Close your account?"
        modalMessage="This will immediately disable your booking page and sign you out of all devices. Your data will be retained and can be restored by contacting support."
        primaryCaption="Yes, close my account"
        secondaryCaption="Cancel"
        primaryClickHandler={handleModalConfirm}
        dismissModalHandler={handleModalDismiss}
        primaryButtonColour="red"
        isPrimaryLoading={isClosing}
      />
    </>
  );
};

export default DeleteAccountForm;
